// ModalFecha.jsx
import React, { useEffect, useContext, useMemo, useCallback, useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	Grid,
	Modal,
	Radio,
	TextField,
	Typography,
	Alert,
	Switch,
	Select,
	MenuItem,
	CircularProgress,
	AlertTitle,
} from "@mui/material";
import { FormNameDesc } from "./FormNameDesc";
import { useTranslation } from "react-i18next";
import { obtenerDatos } from "../../api/config";
import { GlobalContext } from "../../GlobalContext";

const ModalFecha = ({ open, onClose, onExecuteSimulation }) => {
	const { t } = useTranslation();
	const { site_id } = useContext(GlobalContext);

	const [fechaFin, setFechaFin] = useState("");
	const [fechaInicio, setFechaInicio] = useState("");
	const [fechaError, setFechaError] = useState("");
	const [checkedError, setCheckedError] = useState("");
	const [checked, setChecked] = useState("");

	const [formValues, setFormValues] = useState({ name: "", description: "" });
	const [formErrors, setFormErrors] = useState({ name: false, description: false });
	const [showAlert, setShowAlert] = useState(false);
	const [emailNotifications, setEmailNotifications] = useState(false);

	const [forecastOptions, setForecastOptions] = useState([]);
	const [selectedForecast, setSelectedForecast] = useState("");
	const [showForecastError, setShowForecastError] = useState(false);
	const [isLoadingForecast, setIsLoadingForecast] = useState(false);
	const [noFilesForOrigin, setNoFilesForOrigin] = useState(false);
	const [allowRunWithoutForecast, setAllowRunWithoutForecast] = useState(false);
	const [hideInfoAlert, setHideInfoAlert] = useState(false);

	const originReadable = useMemo(() => {
		if (checked === "KRONOS") return "UKG";
		if (checked === "DB") return "la base de datos (DB)";
		return "el origen seleccionado";
	}, [checked]);

	const handleFormChange = (name, value) => {
		setFormValues((p) => ({ ...p, [name]: value }));
		setFormErrors((p) => ({ ...p, [name]: false }));
	};

	// Normaliza a YYYY-MM
	const normYm = (v) => {
		if (!v) return null;
		const s = String(v).trim();
		const mIso = s.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?(?:[T\s].*)?$/);
		if (mIso) {
			const y = mIso[1];
			const mm = String(Number(mIso[2])).padStart(2, "0");
			return `${y}-${mm}`;
		}
		const m = s.match(/^(\d{4})[-/]?(\d{1,2})$/);
		if (m) {
			const y = m[1];
			const mm = String(Number(m[2])).padStart(2, "0");
			return `${y}-${mm}`;
		}
		return null;
	};

	// Carga opciones según origen
	useEffect(() => {
		setAllowRunWithoutForecast(false);
		if (checked === "DB" || checked === "KRONOS") {
			let cancelled = false;

			setFechaInicio("");
			setFechaFin("");
			setSelectedForecast("");

			const fetchForecastOptions = async () => {
				setIsLoadingForecast(true);
				try {
					const data = await obtenerDatos(
						`/forecast-files/forecast_by_user?site_id=${site_id}&forecast_origin=${checked}`
					);
					const list = Array.isArray(data)
						? data
						: Array.isArray(data?.forecast)
						? data.forecast
						: Object.values(data?.forecast ?? {});

					const mapped = list.map((it) => {
						const raw = String(it.origen ?? "").toUpperCase();
						const origin = /KRONOS|UKG/.test(raw)
							? "KRONOS"
							: /DB|DATABASE|BASE\s*DE\s*DATOS|BBDD/.test(raw)
							? "DB"
							: raw || "";

						const start = normYm(it.mes_inicio);
						const end = normYm(it.mes_fin);

						const displayStart = normYm(it.mes_inicio) ?? start;
						const displayEnd = normYm(it.mes_fin) ?? end;

						return {
							label: it.nombre,
							value: it.id,
							origin,
							start,
							end,
							displayStart,
							displayEnd,
						};
					});

					const onlySameOrigin = mapped.filter((o) => o.origin === checked);

					setForecastOptions(onlySameOrigin);
					const noFiles = onlySameOrigin.length === 0;
					setNoFilesForOrigin(noFiles);
					setShowForecastError(false);
				} catch (error) {
					console.error("Error al obtener forecast:", error);
					if (!cancelled) {
						setForecastOptions([]);
						setNoFilesForOrigin(true);
						setShowForecastError(false);
					}
				} finally {
					if (!cancelled) {
						setIsLoadingForecast(false);
						setSelectedForecast("");
					}
				}
			};

			fetchForecastOptions();
			return () => {
				cancelled = true;
			};
		} else {
			setSelectedForecast("");
			setForecastOptions([]);
			setShowForecastError(false);
			setFechaInicio("");
			setFechaFin("");
			setNoFilesForOrigin(false);
		}
	}, [checked, site_id]);

	const areDatesValid = useMemo(() => {
		if (!(fechaInicio && fechaFin)) return false;
		const [yi, mi] = fechaInicio.split("-");
		const [yf, mf] = fechaFin.split("-");
		const iniTotal = Number(yi) * 12 + Number(mi);
		const finTotal = Number(yf) * 12 + Number(mf);
		return iniTotal <= finTotal;
	}, [fechaInicio, fechaFin]);

	const canEditDates = !!checked && !isLoadingForecast;
	const requiresForecast = checked === "DB" || checked === "KRONOS";

	const toYm = useCallback((ym) => {
		if (!ym) return null;
		const [y, m] = ym.split("-");
		return Number(y) * 12 + Number(m);
	}, []);

	const doesForecastCoverSelectedRange = useCallback(
		(opt) => {
			if (!fechaInicio || !fechaFin) return true;
			const selStart = toYm(fechaInicio);
			const selEnd = toYm(fechaFin);
			const fStart = toYm(opt.start);
			const fEnd = toYm(opt.end);
			if (fStart == null || fEnd == null) return false;
			return fStart <= selStart && fEnd >= selEnd;
		},
		[fechaInicio, fechaFin, toYm]
	);

	const filteredOptions = useMemo(
		() => (forecastOptions || []).filter(doesForecastCoverSelectedRange),
		[forecastOptions, doesForecastCoverSelectedRange]
	);

	// Si cambia el rango y la selección ya no calza, se invalida
	useEffect(() => {
		if (!selectedForecast) return;
		const found = (forecastOptions || []).find((o) => o.value === selectedForecast);
		if (!found || !doesForecastCoverSelectedRange(found)) {
			setSelectedForecast("");
		}
	}, [forecastOptions, selectedForecast, doesForecastCoverSelectedRange]);

	const hasAnyOptions = (forecastOptions?.length ?? 0) > 0;
	const needDates = !!checked && !isLoadingForecast && !areDatesValid;
	const filteredEmpty = areDatesValid && hasAnyOptions && filteredOptions.length === 0;

	const shouldShowInfoAlert = requiresForecast && !isLoadingForecast && !!checked;

	const infoMessageKey = useMemo(() => {
		if (!shouldShowInfoAlert) return null;

		if (needDates) {
			// Aún no hay fechas: mensaje neutro
			return "aux.messages.pick_dates_or_run_from_origin";
		}

		if (areDatesValid && !hasAnyOptions && !isLoadingForecast) {
			// Con fechas válidas pero el endpoint no trae nada para el origen
			return "aux.messages.no_files_for_origin_confirm";
		}

		if (filteredEmpty) {
			// Hay archivos del origen, pero NINGUNO cubre el rango
			return "aux.messages.no_files_for_dates_confirm";
		}

		// Hay opciones para el rango seleccionado
		return "aux.messages.you_can_select_or_run_from_origin";
	}, [
		shouldShowInfoAlert,
		needDates,
		areDatesValid,
		hasAnyOptions,
		isLoadingForecast,
		filteredEmpty,
	]);

	const canSelectForecast =
		!!checked &&
		!isLoadingForecast &&
		areDatesValid &&
		!allowRunWithoutForecast &&
		(forecastOptions?.length ?? 0) > 0;

	const canExecute =
		!isLoadingForecast &&
		!!checked &&
		areDatesValid &&
		(allowRunWithoutForecast || !requiresForecast || !!selectedForecast) &&
		formValues.name.trim() !== "" &&
		formValues.description.trim() !== "";

	const handleButtonClick = () => {
		const errors = {
			name: formValues.name.trim() === "",
			description: formValues.description.trim() === "",
		};
		setFormErrors(errors);

		if (!(fechaInicio && fechaFin)) {
			setFechaError(t("aux.messages.date_obligatory"));
			return;
		}
		if (!areDatesValid) {
			setFechaError(t("aux.messages.date_error"));
			return;
		}
		setFechaError("");

		if (!checked) {
			setCheckedError(t("aux.messages.select_checkbox"));
			return;
		} else {
			setCheckedError("");
		}

		if (errors.name || errors.description) {
			setShowAlert(true);
			return;
		} else {
			setShowAlert(false);
		}

		onExecuteSimulation(
			fechaInicio,
			fechaFin,
			checked,
			formValues,
			emailNotifications,
			allowRunWithoutForecast && allowRunWithoutForecast.fromOrigin
				? { fromOrigin: true }
				: selectedForecast
		);

		handleCloseAndReset();
	};

	const handleRadioChange = (event) => {
		const { name } = event.target;
		setChecked(name);
	};

	useEffect(() => {
		setAllowRunWithoutForecast(false);
		setHideInfoAlert(false);
	}, [checked, open]);

	const handleCloseAndReset = (e) => {
		setHideInfoAlert(true);
		setChecked("");
		setFechaError("");
		setFechaFin("");
		setFechaInicio("");
		setFormValues({ name: "", description: "" });
		setFormErrors({ name: false, description: false });
		setShowAlert(false);
		setSelectedForecast("");
		setForecastOptions([]);
		setAllowRunWithoutForecast(false);
		onClose(e); // <- cierra el modal (el padre debe setOpen(false))
	};

	return (
		<Modal
			open={open}
			onClose={handleCloseAndReset}
			sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: "60%",
					backgroundColor: "background.paper",
					border: "1px",
					borderRadius: "4px",
					borderColor: "secondary.main",
					boxShadow: 30,
					p: 2,
					overflowY: "auto",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexDirection: "column",
				}}
			>
				<Typography variant="h6" gutterBottom>
					{t("aux.final_adjustments")}
				</Typography>

				<Grid container spacing={2} sx={{ width: "100%" }}>
					<Grid item xs={12}>
						<FormControl
							required
							error={!!checkedError}
							component="fieldset"
							sx={{
								width: "100%",
								mb: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							variant="standard"
						>
							<FormGroup>
								<FormLabel component="legend">{t("sim.sim_form.origen_demand")}</FormLabel>
								<FormControlLabel
									control={
										<Radio checked={checked === "KRONOS"} onChange={handleRadioChange} name="KRONOS" />
									}
									label="UKG"
								/>
								<FormControlLabel
									control={<Radio checked={checked === "DB"} onChange={handleRadioChange} name="DB" />}
									label={t("sim.sim_form.db")}
								/>
							</FormGroup>
							{checkedError && <FormHelperText>{t("aux.messages.select_checkbox")}</FormHelperText>}
						</FormControl>
					</Grid>
				</Grid>

				<Grid container spacing={2} sx={{ width: "100%" }}>
					<Grid item xs={6}>
						<TextField
							id="dateInicio"
							label={t("aux.date_start")}
							type="month"
							value={fechaInicio}
							onChange={(e) => setFechaInicio(e.target.value)}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%", m: 2 }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
							error={!!fechaError}
							helperText={fechaError && !fechaInicio ? fechaError : ""}
							disabled={!canEditDates && !allowRunWithoutForecast}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="dateFin"
							label={t("aux.date_end")}
							type="month"
							value={fechaFin}
							onChange={(e) => setFechaFin(e.target.value)}
							error={!!fechaError}
							helperText={
								fechaError
									? fechaError
									: fechaInicio && fechaFin && !areDatesValid
									? t("aux.messages.date_error")
									: ""
							}
							InputLabelProps={{ shrink: true }}
							style={{ width: "100%" }}
							InputProps={{ onKeyDown: (e) => e.preventDefault() }}
							disabled={!canEditDates && !allowRunWithoutForecast}
						/>
					</Grid>

					{/* SELECT de forecasts (se oculta si el usuario decide continuar sin archivo) */}
					{(checked === "DB" || checked === "KRONOS") && !allowRunWithoutForecast && (
						<Grid item xs={12}>
							<FormControl
								required
								fullWidth
								error={showForecastError && !noFilesForOrigin}
								sx={{ mt: 1 }}
							>
								<FormLabel>{t("sim.sim_form.select_forecast")}</FormLabel>
								<Select
									value={selectedForecast}
									onChange={(e) => setSelectedForecast(e.target.value)}
									displayEmpty
									disabled={!canSelectForecast}
								>
									{isLoadingForecast ? (
										<MenuItem disabled>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<CircularProgress size={18} />
												<span>{t("aux.messages.loading")}</span>
											</Box>
										</MenuItem>
									) : filteredOptions.length > 0 ? (
										filteredOptions.map((opt) => (
											<MenuItem key={`${opt.value}`} value={opt.value}>
												{`${opt.label}${
													opt.displayStart && opt.displayEnd
														? ` (${opt.displayStart} \u2192 ${opt.displayEnd})`
														: ""
												}`}
											</MenuItem>
										))
									) : (
										<MenuItem disabled>— {t("aux.messages.no_forecasts_in_range")} —</MenuItem>
									)}
								</Select>

								{/* avisos secundarios */}
								{!isLoadingForecast &&
									fechaInicio &&
									fechaFin &&
									forecastOptions.length > 0 &&
									filteredOptions.length === 0 && (
										<>
											<FormHelperText>{t("aux.messages.no_forecasts_in_range")}</FormHelperText>
										</>
									)}

								{noFilesForOrigin && (
									<Box sx={{ mt: 1 }}>
										<Alert severity="warning" variant="outlined">
											{t("aux.messages.no_files")}
										</Alert>
									</Box>
								)}

								{showForecastError && !noFilesForOrigin && (
									<FormHelperText>{t("aux.messages.select_forecast_error")}</FormHelperText>
								)}
							</FormControl>
						</Grid>
					)}

					{shouldShowInfoAlert && infoMessageKey && !allowRunWithoutForecast && !hideInfoAlert && (
						<Grid item xs={12}>
							<Alert
								severity="info"
								variant="outlined"
								sx={(theme) => ({
									borderRadius: 3,
									borderWidth: 2,
									borderColor: theme.palette.info.main,
									bgcolor: theme.palette.mode === "dark" ? "transparent" : `${theme.palette.info.main}14`, // leve tinte
									"& .MuiAlert-icon": { color: theme.palette.info.main },
								})}
							>
								<AlertTitle>{t("aux.messages.alert.info")}</AlertTitle>
								<Typography sx={{ color: "text.secondary" }}>
									{t(infoMessageKey, { origin: originReadable })}
								</Typography>

								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: 2 }}>
									<Button
										variant="text"
										onClick={() => {
											setAllowRunWithoutForecast({ fromOrigin: true });
											setHideInfoAlert(true);
										}}
										sx={{ fontWeight: 700, textTransform: "none", color: "info.main" }}
									>
										{t("aux.buttons.run_from_origin", { origin: originReadable })}
									</Button>
									<Button
										variant="text"
										onClick={handleCloseAndReset}
										sx={{ fontWeight: 700, textTransform: "none", color: "text.primary" }}
									>
										{t("aux.buttons.go_back")}
									</Button>
								</Box>
							</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<FormNameDesc
							formValues={formValues}
							formErrors={formErrors}
							onFormChange={handleFormChange}
						/>
					</Grid>

					{showAlert && (
						<Grid item xs={12}>
							<Alert severity="error">{t("aux.messages.error_campos")}</Alert>
						</Grid>
					)}

					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={emailNotifications}
									onChange={(e) => setEmailNotifications(e.target.checked)}
								/>
							}
							label={t("sim.sim_form.email_notify")}
						/>
					</Grid>

					<Grid item xs={12}>
						<Button
							variant="contained"
							color="primary"
							onClick={handleButtonClick}
							sx={{ width: "100%" }}
							disabled={!canExecute}
						>
							{t("aux.buttons.sim.execute")}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Modal>
	);
};

export { ModalFecha };
